import { api, ApiError } from '../api';
import { el, errorBanner , appendAll} from '../components/layout';

type Client = {
  id: number;
  name: string;
  website_url: string;
  market: string;
};

type Icp = {
  id: number;
  client_id: number | null;
  client_name: string;
  website_url: string;
  market: string;
  geo_cities: string;
  categories: string;
  summary: string | null;
  product_context: string | null;
  raw_json?: string | null;
};

const DEFAULT_EXPORT_FIELDS = [
  'Company',
  'City',
  'Category',
  'Website',
  'Phone',
  'Instagram',
  'Decision Maker',
  'Title',
  'Email',
  'Fit Score',
  'Tier',
  'Why Good Buyer',
  'Likely Products',
  'Call Notes',
  'Source',
  'Last Verified',
];

function parseList(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {
    /* comma list */
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function chipInput(
  initial: string[],
  onChange: (values: string[]) => void,
): { root: HTMLElement; get: () => string[]; set: (v: string[]) => void } {
  let values = [...initial];
  const root = el('div');
  const input = el('input', {
    className: 'input',
    placeholder: 'Type and press Enter or comma',
  }) as HTMLInputElement;
  const chips = el('div', { className: 'chips' });

  function paint() {
    chips.replaceChildren(
      ...values.map((v) =>
        el('span', { className: 'chip' }, [
          document.createTextNode(v),
          el('button', {
            type: 'button',
            text: 'x',
            onClick: () => {
              values = values.filter((x) => x !== v);
              onChange(values);
              paint();
            },
          }),
        ]),
      ),
    );
  }

  function addFromInput() {
    const parts = input.value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    for (const p of parts) {
      if (!values.includes(p)) values.push(p);
    }
    input.value = '';
    onChange(values);
    paint();
  }

  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' || ev.key === ',') {
      ev.preventDefault();
      addFromInput();
    }
  });
  input.addEventListener('blur', addFromInput);

  appendAll(root, input, chips);
  paint();
  return {
    root,
    get: () => [...values],
    set: (v) => {
      values = [...v];
      onChange(values);
      paint();
    },
  };
}

export function renderNewRunPage(root: HTMLElement) {
  const content = el('div');
  appendAll(content, el('div', { className: 'page-header' }, [
      el('h1', { text: 'New Run' }),
      el('a', { href: '#/runs', className: 'btn', text: 'Cancel' }),
    ]),
  );

  const host = el('div');
  content.appendChild(host);
  root.appendChild(content);

  let clients: Client[] = [];
  let savedIcps: Icp[] = [];

  let clientMode: 'existing' | 'new' = 'existing';
  let selectedClientId: number | null = null;
  let icpSource: 'analyze' | 'manual' | 'saved' = 'analyze';
  let selectedIcpId: number | null = null;

  let market = 'socal';
  let summary = '';
  let productContext = '';
  let rawJson = '';
  let websiteUrl = '';
  let clientName = '';
  let geoCities: string[] = [];
  let categories: string[] = [];
  let targetCount = 20;
  let enrichPaid = false;
  let exportFields = [...DEFAULT_EXPORT_FIELDS];
  let analyzing = false;
  let submitting = false;

  async function loadClients() {
    try {
      const data = await api<{ items: Client[] }>('/api/clients');
      clients = data.items || [];
      paint();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      host.replaceChildren(errorBanner(msg, loadClients));
    }
  }

  async function loadIcpsForClient(clientId: number) {
    try {
      const data = await api<{ items: Icp[] }>(`/api/icp?client_id=${clientId}`);
      savedIcps = data.items || [];
    } catch {
      savedIcps = [];
    }
  }

  function applyIcp(icp: Icp) {
    market = icp.market || market;
    summary = icp.summary || '';
    productContext = icp.product_context || '';
    rawJson = icp.raw_json || '';
    websiteUrl = icp.website_url || websiteUrl;
    clientName = icp.client_name || clientName;
    geoCities = parseList(icp.geo_cities || '[]');
    categories = parseList(icp.categories || '[]');
    selectedIcpId = icp.id;
  }

  function paint() {
    const step1 = el('div', { className: 'card' });
    step1.appendChild(el('div', { className: 'step-title', text: 'Step 1 · Client and ICP' }));

    const clientField = el('div', { className: 'field' });
    clientField.appendChild(el('label', { className: 'label', text: 'Client' }));
    const clientSelect = el('select', { className: 'select' }) as HTMLSelectElement;
    clientSelect.appendChild(el('option', { value: '', text: 'Select a client...' }));
    for (const c of clients) {
      appendAll(clientSelect, el('option', {
          value: String(c.id),
          text: `${c.name} (${c.market})`,
          ...(selectedClientId === c.id ? { selected: true } : {}),
        }),
      );
    }
    clientSelect.appendChild(el('option', { value: '__new__', text: 'New client...' }));
    if (clientMode === 'new') clientSelect.value = '__new__';
    else if (selectedClientId) clientSelect.value = String(selectedClientId);

    clientSelect.addEventListener('change', async () => {
      if (clientSelect.value === '__new__') {
        clientMode = 'new';
        selectedClientId = null;
        paint();
        return;
      }
      clientMode = 'existing';
      selectedClientId = Number(clientSelect.value) || null;
      const c = clients.find((x) => x.id === selectedClientId);
      if (c) {
        clientName = c.name;
        websiteUrl = c.website_url;
        market = c.market || market;
        await loadIcpsForClient(c.id);
        if (savedIcps[0]) {
          icpSource = 'saved';
          applyIcp(savedIcps[0]);
        }
      }
      paint();
    });
    clientField.appendChild(clientSelect);
    step1.appendChild(clientField);

    if (clientMode === 'new') {
      const nameIn = el('input', {
        className: 'input',
        value: clientName,
        placeholder: 'Client name',
      }) as HTMLInputElement;
      nameIn.addEventListener('input', () => {
        clientName = nameIn.value;
      });
      const urlIn = el('input', {
        className: 'input',
        value: websiteUrl,
        placeholder: 'https://example.com',
      }) as HTMLInputElement;
      urlIn.addEventListener('input', () => {
        websiteUrl = urlIn.value;
      });
      const marketIn = el('input', {
        className: 'input',
        value: market,
        placeholder: 'market',
      }) as HTMLInputElement;
      marketIn.addEventListener('input', () => {
        market = marketIn.value;
      });
      appendAll(step1, el('div', { className: 'field' }, [
          el('label', { className: 'label', text: 'Name' }),
          nameIn,
        ]),
        el('div', { className: 'field' }, [
          el('label', { className: 'label', text: 'Website URL' }),
          urlIn,
        ]),
        el('div', { className: 'field' }, [
          el('label', { className: 'label', text: 'Market' }),
          marketIn,
        ]),
      );
    }

    const sourceRow = el('div', { className: 'field' });
    sourceRow.appendChild(el('label', { className: 'label', text: 'ICP source' }));
    const radios = el('div', { className: 'radio-row' });
    for (const [val, label] of [
      ['analyze', 'Analyze website (AI)'],
      ['manual', 'Enter manually'],
      ['saved', 'Use saved ICP'],
    ] as const) {
      const id = `icp-${val}`;
      const lab = el('label', { for: id });
      const radio = el('input', {
        type: 'radio',
        id,
        name: 'icp-source',
        value: val,
      }) as HTMLInputElement;
      if (icpSource === val) radio.checked = true;
      radio.addEventListener('change', () => {
        icpSource = val;
        paint();
      });
      appendAll(lab, radio, document.createTextNode(` ${label}`));
      radios.appendChild(lab);
    }
    sourceRow.appendChild(radios);
    step1.appendChild(sourceRow);

    if (icpSource === 'saved') {
      const sel = el('select', { className: 'select' }) as HTMLSelectElement;
      if (!savedIcps.length) {
        sel.appendChild(el('option', { value: '', text: 'No saved ICPs for this client' }));
      } else {
        for (const icp of savedIcps) {
          appendAll(sel, el('option', {
              value: String(icp.id),
              text: `#${icp.id} · ${icp.market} · ${parseList(icp.categories).slice(0, 3).join(', ')}`,
              ...(selectedIcpId === icp.id ? { selected: true } : {}),
            }),
          );
        }
        sel.addEventListener('change', () => {
          const icp = savedIcps.find((x) => x.id === Number(sel.value));
          if (icp) {
            applyIcp(icp);
            paint();
          }
        });
      }
      appendAll(step1, el('div', { className: 'field' }, [
          el('label', { className: 'label', text: 'Saved ICP' }),
          sel,
        ]),
      );
    }

    if (icpSource === 'analyze') {
      const btn = el('button', {
        className: 'btn btn-primary',
        type: 'button',
        text: analyzing ? 'Analyzing...' : 'Analyze',
        disabled: analyzing,
        onClick: async () => {
          const url = websiteUrl.trim();
          if (!url) {
            alert('Website URL required');
            return;
          }
          analyzing = true;
          paint();
          try {
            const data = await api<{ ok?: boolean; icp?: Record<string, unknown>; error?: string }>(
              '/api/icp/analyze',
              {
                method: 'POST',
                body: JSON.stringify({
                  website_url: url,
                  hint_name: clientName || undefined,
                }),
              },
            );
            if (!data.icp) throw new Error(data.error || 'Analyze failed');
            const icp = data.icp;
            market = String(icp.market || market);
            summary = String(icp.summary || '');
            productContext = String(icp.product_context || '');
            rawJson = typeof icp.raw_json === 'string' ? icp.raw_json : JSON.stringify(icp);
            clientName = String(icp.client_name || clientName);
            websiteUrl = String(icp.website_url || websiteUrl);
            geoCities = Array.isArray(icp.geo_cities)
              ? (icp.geo_cities as string[])
              : parseList(String(icp.geo_cities || '[]'));
            categories = Array.isArray(icp.categories)
              ? (icp.categories as string[])
              : parseList(String(icp.categories || '[]'));
            selectedIcpId = null;
          } catch (err) {
            alert(err instanceof Error ? err.message : String(err));
          } finally {
            analyzing = false;
            paint();
          }
        },
      });
      step1.appendChild(el('div', { className: 'field' }, [btn]));
    }

    const marketIn = el('input', {
      className: 'input',
      value: market,
    }) as HTMLInputElement;
    marketIn.addEventListener('input', () => {
      market = marketIn.value;
    });

    const geo = chipInput(geoCities, (v) => {
      geoCities = v;
    });
    const cats = chipInput(categories, (v) => {
      categories = v;
    });

    const summaryTa = el('textarea', {
      className: 'textarea',
      text: summary,
    }) as HTMLTextAreaElement;
    summaryTa.value = summary;
    summaryTa.addEventListener('input', () => {
      summary = summaryTa.value;
    });

    const productTa = el('textarea', {
      className: 'textarea',
      text: productContext,
    }) as HTMLTextAreaElement;
    productTa.value = productContext;
    productTa.addEventListener('input', () => {
      productContext = productTa.value;
    });

    appendAll(step1, el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Market' }),
        marketIn,
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Geo cities' }),
        geo.root,
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Categories' }),
        cats.root,
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Summary' }),
        summaryTa,
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Product context' }),
        productTa,
      ]),
    );

    const step2 = el('div', { className: 'card' });
    step2.appendChild(el('div', { className: 'step-title', text: 'Step 2 · Run config' }));

    const targetIn = el('input', {
      className: 'input',
      type: 'number',
      min: '1',
      max: '200',
      value: String(targetCount),
      style: 'max-width:120px',
    }) as HTMLInputElement;
    targetIn.addEventListener('input', () => {
      targetCount = Number(targetIn.value) || 20;
    });

    const enrich = el('input', { type: 'checkbox' }) as HTMLInputElement;
    enrich.checked = enrichPaid;
    enrich.addEventListener('change', () => {
      enrichPaid = enrich.checked;
    });

    const fieldsWrap = el('div', { className: 'export-fields' });
    for (const f of DEFAULT_EXPORT_FIELDS) {
      const id = `field-${f}`;
      const lab = el('label', { for: id });
      const cb = el('input', { type: 'checkbox', id }) as HTMLInputElement;
      cb.checked = exportFields.includes(f);
      cb.addEventListener('change', () => {
        if (cb.checked) {
          if (!exportFields.includes(f)) exportFields.push(f);
        } else {
          exportFields = exportFields.filter((x) => x !== f);
        }
      });
      appendAll(lab, cb, document.createTextNode(` ${f}`));
      fieldsWrap.appendChild(lab);
    }

    appendAll(step2, el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Target count (1-200)' }),
        targetIn,
      ]),
      el('div', { className: 'field check-row' }, [
        el('label', {}, [
          enrich,
          document.createTextNode(' Paid enrichment (Apollo, Tier A)'),
        ]),
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Export fields' }),
        fieldsWrap,
      ]),
    );

    const submit = el('button', {
      className: 'btn btn-primary',
      type: 'button',
      text: submitting ? 'Starting...' : 'Start run',
      disabled: submitting,
      onClick: async () => {
        geoCities = geo.get();
        categories = cats.get();
        if (!clientName.trim()) {
          alert('Client name required');
          return;
        }
        if (!geoCities.length || !categories.length) {
          alert('geo_cities and categories required');
          return;
        }
        submitting = true;
        paint();
        try {
          let clientId = selectedClientId;
          if (clientMode === 'new') {
            const created = await api<{ client: Client }>('/api/clients', {
              method: 'POST',
              body: JSON.stringify({
                name: clientName.trim(),
                website_url: websiteUrl.trim() || `https://example.invalid/${Date.now()}`,
                market: market.trim() || 'socal',
              }),
            });
            clientId = created.client.id;
            websiteUrl = created.client.website_url;
          }

          const data = await api<{ id: number; trigger_ok?: boolean; trigger_error?: string }>(
            '/api/runs',
            {
              method: 'POST',
              body: JSON.stringify({
                client: clientName.trim(),
                client_id: clientId,
                icp_profile_id: icpSource === 'saved' ? selectedIcpId : null,
                website_url: websiteUrl.trim() || null,
                market: market.trim() || 'socal',
                geo_cities: geoCities,
                categories,
                target_count: targetCount,
                export_fields: exportFields,
                enrich_paid: enrichPaid,
                save_icp: true,
                summary,
                product_context: productContext,
                raw_json: rawJson || undefined,
              }),
            },
          );

          if (data.trigger_ok === false && data.trigger_error) {
            alert(
              `Run #${data.id} created but workflow trigger failed: ${data.trigger_error}`,
            );
          }
          location.hash = `#/runs/${data.id}`;
        } catch (err) {
          alert(err instanceof Error ? err.message : String(err));
          submitting = false;
          paint();
        }
      },
    });

    host.replaceChildren(step1, step2, el('div', { className: 'row-actions' }, [submit]));
  }

  loadClients();
}
